import { Refresh } from "@mui/icons-material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import {
  Alert,
  Box,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ROLES } from "app/constants";
import { useAppSelector } from "app/hooks";
import { Autocomplete, ButtonGroup, PageHeader, Spacer } from "common";
import DownloadButton from "common/components/DownloadButton/DownloadButton";
import DateRangeSelector2 from "components/DateRangeSeletor/DateRangeSelector2";
import useDictionaries from "features/dictionaries/useDictionaries";
import { selectProfile } from "features/profile/profileSlice";
import useAccess from "features/profile/useAccess";
import useSettings from "features/settings/useSettings";
import { formatPrice } from "helpers/priceHelper";
import I18n from "i18n";
import moment from "moment";
import { useEffect, useState } from "react";
import {
  BookingCommissionItem,
  CommissionReportResponse,
  DayCommissionItem,
  getCommissionReport,
} from "./commissionReportApi";

export default function CommissionReportView() {
  const profile = useAppSelector(selectProfile);
  const { hasPermissions, hasRoles } = useAccess();
  const { dictionaries } = useDictionaries();
  const [settings, setSettings] = useSettings({ key: "commissionReport" });
  const isAdmin = !hasRoles(ROLES.PARTNER);

  const [partnerId, setPartnerId] = useState<number | null>(profile.partner_id);
  const [range, setRange] = useState({
    start: moment().startOf("month").format("YYYY-MM-DD"),
    end: moment().endOf("month").format("YYYY-MM-DD"),
  });

  const [data, setData] = useState<CommissionReportResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (partnerId) {
      loadReport();
    }
  }, [
    partnerId,
    range,
    settings.groupBy,
    settings.currency,
    false,
    settings.currency === "HUF" ? 0 : 2,
  ]);

  async function loadReport() {
    if (!partnerId) return;
    setLoading(true);
    const response = await getCommissionReport(
      partnerId,
      range.start,
      range.end,
      settings.groupBy || "day",
      settings.currency || "EUR",
    );
    if (response.success && response.data) {
      setData(response.data);
    }
    setLoading(false);
  }

  return (
    <>
      <PageHeader
        avatar={<AssessmentIcon />}
        title={I18n.t("CommissionReport.title")}
      >
        <ButtonGroup
          size="small"
          options={[
            { value: "EUR", label: "EUR" },
            { value: "HUF", label: "HUF" },
          ]}
          value={settings.currency || "EUR"}
          onChange={(evt: any) => {
            setSettings({
              currency: evt.target.value,
            });
          }}
        />
        <IconButton
          disabled={!partnerId}
          onClick={() => {
            loadReport();
          }}
        >
          <Refresh />
        </IconButton>
      </PageHeader>
      <Card sx={{ mb: 2, position: "relative" }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {isAdmin && (
              <>
                <Autocomplete
                  fullWidth
                  value={partnerId || ""}
                  onChange={(e: any) => setPartnerId(Number(e.target.value))}
                  label={I18n.t("CommissionReport.partner")}
                  options={dictionaries.partners}
                  helperText={
                    partnerId ? undefined : I18n.t("CommissionReport.noPartner")
                  }
                />
              </>
            )}
            <DateRangeSelector2
              numberOfCalendars={1}
              numberOfYears={0}
              value={
                range.start && range.end
                  ? {
                      start: moment(range.start),
                      end: moment(range.end),
                    }
                  : undefined
              }
              onChange={(start, end) => {
                setRange(
                  start && end
                    ? {
                        start: start.format("YYYY-MM-DD"),
                        end: end.format("YYYY-MM-DD"),
                      }
                    : { start: null as any, end: null as any },
                );
              }}
              dateButtonMinWidth={150}
            />
            <ButtonGroup
              size="small"
              options={[
                {
                  value: "booking",
                  label: I18n.t("CommissionReport.byBooking"),
                },
                { value: "day", label: I18n.t("CommissionReport.byDay") },
              ]}
              value={settings.groupBy || "day"}
              onChange={(evt: any) => {
                setSettings({
                  groupBy: evt.target.value,
                });
              }}
            />
            <Spacer right />
            <DownloadButton
              url={`/partners/${partnerId}/commission-report/excel`}
              params={{
                from: range.start,
                to: range.end,
                group_by: settings.groupBy || "day",
                currency: settings.currency || "EUR",
              }}
              filename={`jutalék_riport_${range.start}-${range.end}.xlsx`}
              disabled={!partnerId}
            >
              Letöltés
            </DownloadButton>
          </Box>
          {loading && (
            <LinearProgress
              sx={{ position: "absolute", bottom: 0, left: 0, right: 0 }}
            />
          )}
        </CardContent>
      </Card>

      {!!data && data.items.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {I18n.t("CommissionReport.noData")}
        </Alert>
      )}
      {!!data && data.items.length > 0 && (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {settings.groupBy === "booking" ? (
                    <>
                      <TableCell>
                        {I18n.t("CommissionReport.bookingNumber")}
                      </TableCell>
                      <TableCell>{I18n.t("CommissionReport.date")}</TableCell>
                      <TableCell>
                        {I18n.t("CommissionReport.program")}
                      </TableCell>
                      <TableCell>
                        {I18n.t("CommissionReport.variant")}
                      </TableCell>
                      <TableCell align="right">
                        {I18n.t("CommissionReport.headcount")}
                      </TableCell>
                      <TableCell align="right">
                        {I18n.t("CommissionReport.bookingAmount")}
                      </TableCell>
                      <TableCell align="right">
                        {I18n.t("CommissionReport.commissionRate")}
                      </TableCell>
                      <TableCell align="right">
                        {I18n.t("CommissionReport.commissionAmount")}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>{I18n.t("CommissionReport.date")}</TableCell>
                      <TableCell align="right">
                        {I18n.t("CommissionReport.bookingCount")}
                      </TableCell>
                      <TableCell align="right">
                        {I18n.t("CommissionReport.totalHeadcount")}
                      </TableCell>
                      <TableCell align="right">
                        {I18n.t("CommissionReport.totalBookingAmount")}
                      </TableCell>
                      <TableCell align="right">
                        {I18n.t("CommissionReport.totalCommissionAmount")}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {settings.groupBy === "booking"
                  ? (data.items as BookingCommissionItem[]).map(item => (
                      <TableRow key={`${item.booking_id}${item.date}`}>
                        <TableCell>{item.booking_number}</TableCell>
                        <TableCell>{moment(item.date).format("L")}</TableCell>
                        <TableCell>{item.program_name}</TableCell>
                        <TableCell>{item.variant_name}</TableCell>
                        <TableCell align="right">{item.headcount}</TableCell>
                        <TableCell align="right">
                          {formatPrice(
                            item.booking_amount,
                            settings.currency || "EUR",
                            false,
                            settings.currency === "HUF" ? 0 : 2,
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {item.commission_rate}%
                        </TableCell>
                        <TableCell align="right">
                          {formatPrice(
                            item.commission_amount,
                            settings.currency || "EUR",
                            false,
                            settings.currency === "HUF" ? 0 : 2,
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  : (
                      data.items as (DayCommissionItem & {
                        booking_id?: number;
                      })[]
                    ).map(item => (
                      <TableRow key={`${item.booking_id}${item.date}`}>
                        <TableCell>{moment(item.date).format("L")}</TableCell>
                        <TableCell align="right">
                          {item.booking_count}
                        </TableCell>
                        <TableCell align="right">
                          {item.total_headcount}
                        </TableCell>
                        <TableCell align="right">
                          {formatPrice(
                            item.total_booking_amount,
                            settings.currency || "EUR",
                            false,
                            settings.currency === "HUF" ? 0 : 2,
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {formatPrice(
                            item.total_commission_amount,
                            settings.currency || "EUR",
                            false,
                            settings.currency === "HUF" ? 0 : 2,
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 2, textAlign: "right" }}>
            <Typography variant="h6">
              {I18n.t("CommissionReport.totalCommission")}:{" "}
              {formatPrice(
                data.total_commission,
                settings.currency || "EUR",
                false,
                settings.currency === "HUF" ? 0 : 2,
              )}
            </Typography>
          </Box>
        </>
      )}
    </>
  );
}
